import React, { Component, FormEvent } from 'react';
import Editor from '@monaco-editor/react';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import FormicaLabel from '../../custom/FormicaLabel';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import CustomLoading from '../../custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import { ActionState } from '../../../redux/reducers/ActionReducer';
import { changeAction, changeActionIsUpdate, clearAction } from '../../../redux/actions/ActionActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { EmailActionJsonModel } from '../../../domain/collection/rule/action/ActionDomains';
import FormicaInput from '../../custom/FormicaInput';
import CustomPasswordInput from '../../custom/CustomPasswordInput';
import LanguageUtil from '../../../util/LanguageUtil';
import ComponentName from '../../../constant/ComponentName';

class ActionEmailEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      action: { action },
      component: { module },
      user,
    } = this.props;
    const { loading } = this.state;
    const isKyc = module.includes('kyc');

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'grid grid-cols-8 w-full !max-h-[calc(100vh-15rem)] overflow-y-scroll pb-8'}>
        <div className={'col-span-2 flex flex-col border-r border-f-black-5 px-5 py-5 gap-y-5'}>
          <div className={'flex flex-col gap-y-3'}>
            <p className={'text-primary font-bold text-md tracking-wide'}>
              {LanguageUtil.get({ id: 'pages.action.email.title', defaultMessage: 'INTEGRATION CONFIGURATION' })}
            </p>
            <span className={'flex bg-[#F7F8FA] rounded-[4px] w-full justify-between gap-x-1 px-1 py-1 h-[36px]'}>
              <div
                onClick={() => this.onSelectType('transaction')}
                className={`${action.type == 'transaction' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
              >
                {LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Transaction' })}
              </div>
              {isKyc && user.activeRiskType == 'compliance' && (
                <div
                  onClick={() => this.onSelectType('kyc')}
                  className={`${action.type == 'kyc' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
                >
                  {LanguageUtil.get({ id: 'common.label.kyc', defaultMessage: 'KYC' })}
                </div>
              )}
            </span>
            <div className={'flex flex-col w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
              <FormicaSearchSelectBox
                options={this.getTransactionOptions()}
                rounded
                placeholder={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })}
                disabled={!RoleHelper.canEdit('Action')}
                value={action.transaction}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
                data-testid={`${ComponentName.ACTION}_channel_select`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                type={'number'}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTimeout(e)}
                value={action.timeout}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeout', defaultMessage: 'Timeout (ms)' })}
                data-testid={`${ComponentName.ACTION}_timeout_input`}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md tracking-wide'}>
              {LanguageUtil.get({ id: 'pages.action.email.smtp.title', defaultMessage: 'SMTP CONFIGURATION' })}
            </span>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.host', defaultMessage: 'Host' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeHost(e)}
                value={(action as EmailActionJsonModel).smtpHost}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.host', defaultMessage: 'Enter SMTP host' })}
                data-testid={`${ComponentName.ACTION}_host_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.port', defaultMessage: 'Port' })} />
              <FormicaInput
                type={'number'}
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangePort(e)}
                value={(action as EmailActionJsonModel).smtpPort}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.port', defaultMessage: 'Port' })}
                data-testid={`${ComponentName.ACTION}_port_input`}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-y-3'}>
            <span className={'text-primary font-bold text-md tracking-wide'}>
              {LanguageUtil.get({ id: 'common.label.credential', defaultMessage: 'CREDENTIAL' })}
            </span>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.username', defaultMessage: 'Username' })} />
              <FormicaInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeUser(e)}
                value={(action as EmailActionJsonModel).user}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.username', defaultMessage: 'Username' })}
                data-testid={`${ComponentName.ACTION}_username_input`}
              />
            </div>
            <div className={'w-full'}>
              <FormicaLabel label={LanguageUtil.get({ id: 'common.label.password', defaultMessage: 'Password' })} />
              <CustomPasswordInput
                disabled={!RoleHelper.canEdit('Action')}
                onChange={(e: FormEvent<HTMLInputElement>) => this.onChangePassword(e)}
                value={(action as EmailActionJsonModel).password}
                placeholder={LanguageUtil.get({ id: 'common.label.password', defaultMessage: 'password' })}
                data-testid={`${ComponentName.ACTION}_password_input`}
              />
            </div>
          </div>
        </div>
        <div className={'col-span-6 flex flex-col w-full h-full px-5 py-5 gap-y-2 overflow-scroll'}>
          <span className={'font-bold text-md text-primary tracking-wide'}>
            {LanguageUtil.get({ id: 'pages.action.email.mailInformation', defaultMessage: 'MAIL INFORMATION' })}
          </span>
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.from', defaultMessage: 'From' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.from', defaultMessage: 'From' })}
              value={(action as EmailActionJsonModel).from}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeFrom(e)}
              data-testid={`${ComponentName.ACTION}_from_input`}
            />
          </div>
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.to', defaultMessage: 'To' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.to', defaultMessage: 'To' })}
              value={(action as EmailActionJsonModel).to}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTo(e)}
              data-testid={`${ComponentName.ACTION}_to_input`}
            />
          </div>
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.cc', defaultMessage: 'Cc' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.cc', defaultMessage: 'Cc' })}
              value={(action as EmailActionJsonModel).cc}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeCc(e)}
              data-testid={`${ComponentName.ACTION}_cc_input`}
            />
          </div>
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.bcc', defaultMessage: 'Bcc' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.bcc', defaultMessage: 'Bcc' })}
              value={(action as EmailActionJsonModel).bcc}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeBcc(e)}
              data-testid={`${ComponentName.ACTION}_bcc_input`}
            />
          </div>
          <div className={'flex flex-col gap-y-1'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.subject', defaultMessage: 'Subject' })} />
            <FormicaInput
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.subject', defaultMessage: 'Bcc' })}
              value={(action as EmailActionJsonModel).subject}
              disabled={!RoleHelper.canEdit('Action')}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeSubject(e)}
              data-testid={`${ComponentName.ACTION}_subject_input`}
            />
          </div>
          <div className={'h-[180px] 2xl:h-[200px]'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.body', defaultMessage: 'Body' })} />
            <Editor
              options={{ readOnly: !RoleHelper.canEdit('Action') }}
              className={'!border-[1px] rounded-[6px] py-3 2xl:py-4 mt-1'}
              value={(action as EmailActionJsonModel).body}
              onChange={(value: any) => this.onChangeBody(value)}
              language={'html'}
              data-testid={`${ComponentName.ACTION}_body_input`}
            />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).select('id', 'name');
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: string[] = (await this.repository.findMany<TransactionJsonModel>(transactionQuery)).flatMap((a) => a.name);
      this.setState({ transactions, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.computation?.stop();
  };

  private getTransactionOptions() {
    const {
      action: { action },
    } = this.props;
    if (action.type == 'kyc') {
      return [
        {
          label: LanguageUtil.get({ id: 'common.label.individual', defaultMessage: 'Individual' }),
          value: 'individual',
        },
        {
          label: LanguageUtil.get({ id: 'common.label.entity', defaultMessage: 'Entity' }),
          value: 'entity',
        },
      ];
    } else {
      return this.state.transactions.map((transaction) => {
        return {
          label: transaction,
          value: transaction,
        };
      });
    }
  }

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.transaction = e.value;
    changeAction(action);
  }

  private onChangeTimeout(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.timeout = parseInt(e.currentTarget.value);
    changeAction(action);
  }

  private onChangeHost(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).smtpHost = e.currentTarget.value;
    changeAction(action);
  }

  private onChangePort(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).smtpPort = e.currentTarget.value ? parseInt(e.currentTarget.value) : 0;
    changeAction(action);
  }

  private onChangeUser(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).user = e.currentTarget.value;
    changeAction(action);
  }

  private onChangePassword(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).password = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeFrom(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).from = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeTo(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).to = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeCc(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).cc = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeBcc(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).bcc = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeSubject(e: FormEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).subject = e.currentTarget.value;
    changeAction(action);
  }

  private onChangeBody(e: any) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as EmailActionJsonModel).body = e || '';
    changeAction(action);
  }

  private onSelectType(type: string) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.type = type;
    changeAction(action);
  }
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  transactions: string[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
  component: state.component,
  user: state.auth.user,
});
const mapDispatchToProps = {
  changeActionIsUpdate,
  changeAction,
  clearAction,
  enterComponent,
  leaveComponent,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionEmailEditorComponent));
